// @avantia common-code
import { basicSorter, rangeWorker } from '@avantia/client-and-server-utilities';
import { lodash } from '@avantia/lodash';
import { HpTriggerNavigationEventProps } from '../actions/eventActionTypes';
import { triggerNavigationChangedEvent } from '../actions/eventActions';
import { logError } from '../clientLogging';
import { HpMoveToSectionTypes } from '../reducers/questionSetTools';
import { HpClientSection, HpClientSectionUI, HpQuestionDisplayState } from '../types';
import { createVoidEvent } from './utilities';

export interface HpQuestionSectionWorkerProps {
  index: number;
  isActive: boolean;
  section: HpClientSection;
  sectionId: string;
  sectionUI: HpClientSectionUI;
}

export type HpQuestionSectionWorker = (props: HpQuestionSectionWorkerProps) => boolean | void;

export interface HpSectionSpecifier {
  sectionId: string;
  sectionIndex: number;
}

export interface HpSetActiveSectionOptions {
  bypassMoveToSectionCheck?: true; // most times, this should not be provided.
  isValidationRequired?: boolean; // most times, this should not be provided.
  raiseNavigationChangedEvent?: boolean;
}

export function setActiveSection(
  displayState: HpQuestionDisplayState,
  sectionId: string,
  buttonAction: HpMoveToSectionTypes | undefined,
  raiseNavigationChangedEvent: boolean
): void {
  const { activeSectionId: prevSectionId, sections } = displayState;
  const activeSection = sections[sectionId];
  if (activeSection) {
    if (raiseNavigationChangedEvent && sectionId !== prevSectionId) {
      const payload: HpTriggerNavigationEventProps = {
        getEvent: () => createVoidEvent(),
        activeSection,
        previousSection: sections[prevSectionId],
        buttonAction
      };
      triggerNavigationChangedEvent(payload);
    }

    displayState.activeSectionId = sectionId;
  } else {
    throw new Error(`The sectionId "${sectionId}" cannot be found.`);
  }
}

export function getNextOrPreviousSectionId(
  displayState: HpQuestionDisplayState,
  direction: 'next' | 'prev',
  currentSectionId?: string
): string {
  const { sections, sectionsUI } = displayState;
  let sectionId = currentSectionId;
  const pos: number = sectionId && sections[sectionId] ? sections[sectionId].position : -1;
  forEachSectionInOrder({
    displayState,
    options: { iterate: 'sections-only', reverseOrder: direction === 'prev' },
    worker: ({ section, sectionId: id }) => {
      const { position } = section;
      if (sectionId === undefined) {
        sectionId = id;
        return false;
      }

      if ((direction === 'next' && position > pos) || (direction === 'prev' && position < pos)) {
        if (sectionsUI[id].isVisible) {
          sectionId = id;
          return false;
        }
      }

      return true;
    }
  });

  if (sectionId === undefined || sections[sectionId] === undefined) {
    logError(`There is no "Next Section" following from ${sectionId}!`);
  }

  return sectionId as string;
}

export type HpSectionIteratorOptions = { iterate: 'sections-only' | 'groups-only' | 'sections-and-groups' };

export interface HpSectionInOrderIteratorOptions extends HpSectionIteratorOptions {
  ignoreOrderList?: boolean;
  reverseOrder?: boolean;
}

export interface HpForEachSectionInOrderProps {
  displayState: HpQuestionDisplayState;
  options: HpSectionInOrderIteratorOptions;
  worker: HpQuestionSectionWorker;
}

export function forEachSection(
  displayState: HpQuestionDisplayState,
  options: HpSectionIteratorOptions,
  worker: HpQuestionSectionWorker
): void {
  const { activeSectionId, sections, sectionsUI } = displayState;
  const sectionFilter = createSectionFilter(options);
  let index = 0;
  for (const sectionId in sections) {
    const section = sections[sectionId];
    const sectionUI = sectionsUI[sectionId];
    const shouldExit =
      sectionFilter(section) &&
      worker({ section, sectionUI, sectionId, isActive: sectionId === activeSectionId, index: index++ }) === false;
    if (shouldExit) {
      break;
    }
  }
}

function createSectionFilter(options: HpSectionIteratorOptions): (section: HpClientSection) => boolean {
  const { iterate } = options;
  const iterateSection = iterate !== 'groups-only';
  const iterateGroups = iterate !== 'sections-only';
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  return (section: HpClientSection) => {
    const isRepeater = section.isRepeater === true;
    const hasControl = !!section.controlType;
    return !hasControl && ((!isRepeater && iterateSection) || (isRepeater && iterateGroups));
  };
}

/**
 * Iterates each section using the IDs in 'props.displayState.sectionIdOrder'.
 *
 * What this means is that you almost definitely will not iterate over groups (isRepeater=true).
 * This is the case even when you provide 'sections-and-groups' in the options.
 * @param props that you need to provide.
 */
export function forEachSectionInOrder(props: HpForEachSectionInOrderProps): void {
  const { options } = props;
  const { ignoreOrderList, iterate } = options;

  // The 'sections-only' condition is needed because groups will only be iterated over when ignoring the order.
  // The ordered sections list (the 'else' part) hasn't contained groups since before Feb 2023.
  if (ignoreOrderList === true || iterate !== 'sections-only') {
    forEachSectionIgnoringOrder(props);
  } else {
    forEachSectionUsingOrder(props);
  }
}

export interface HpForEachRepeaterSectionProps {
  displayState: HpQuestionDisplayState;
  worker: HpQuestionRepeaterSectionWorker;
}

export type HpQuestionRepeaterSectionWorker = (
  section: HpClientSection,
  sectionUI: HpClientSectionUI,
  sectionId: string
) => void;

export function forEachRepeaterSection({ displayState, worker }: HpForEachRepeaterSectionProps): void {
  const { sections, sectionsUI } = displayState;
  const sids = lodash.keys(sections);
  sids.forEach((id) => {
    if (sections[id].isRepeater) {
      worker(sections[id], sectionsUI[id], id);
    }
  });
}

function forEachSectionUsingOrder({ displayState, options, worker }: HpForEachSectionInOrderProps): void {
  const { reverseOrder } = options;
  const { activeSectionId, sectionIdOrder: list, sections, sectionsUI } = displayState;
  const sectionFilter = createSectionFilter(options);
  let start = 0;
  let end = list.length;
  if (reverseOrder) {
    start = end - 1;
    end = -1; // exclusive upper bound
  }

  rangeWorker(start, end, (index) => {
    const sectionId = list[index];
    const section = sections[sectionId];
    const shouldExit =
      sectionFilter(section) &&
      worker({
        section,
        sectionUI: sectionsUI[sectionId],
        sectionId,
        isActive: activeSectionId === sectionId,
        index
      }) === false;
    return !shouldExit;
  });
}

function forEachSectionIgnoringOrder({ displayState, options, worker }: HpForEachSectionInOrderProps): void {
  const { reverseOrder } = options;
  const { sectionsUI } = displayState;
  let activeSectionKey: string | undefined;
  const list: HpClientSection[] = [];

  forEachSection(displayState, options, ({ section, sectionId, isActive }) => {
    if (isActive) {
      activeSectionKey = sectionId;
    }

    list.push(section);
  });

  if (!reverseOrder) {
    list.sort(sectionSorter);
  } else {
    list.sort((a, b) => -sectionSorter(a, b));
  }

  for (let index = 0; index < list.length; index++) {
    const section = list[index];
    const sectionId = section.sectionId;
    const shouldExit =
      worker({
        section,
        sectionUI: sectionsUI[sectionId],
        sectionId,
        isActive: activeSectionKey === sectionId,
        index
      }) === false;
    if (shouldExit) {
      break;
    }
  }
}

function sectionSorter(s1: HpClientSection, s2: HpClientSection): number {
  const { isRepeater: s1repeat, position: s1pos, sectionId: s1id, title: s1title } = s1;
  const { isRepeater: s2repeat, position: s2pos, sectionId: s2id, title: s2title } = s2;
  let res = !!s1repeat !== !!s2repeat ? (s1repeat ? 1 : -1) : 0;
  if (res === 0) {
    res = basicSorter(s1pos, s2pos);
    if (res === 0) {
      res = basicSorter(s1title, s2title);
      if (res === 0) {
        res = basicSorter(s1id, s2id);
      }
    }
  }

  return res;
}
